import { createApi } from '@reduxjs/toolkit/query/react'
import { original } from '@reduxjs/toolkit'
import { BACKEND_API_URL, CURSOR_LIST_PAGE_SIZE, urlApi } from '../consts/api'
import { appendCommentsSortQueryParams, DEFAULT_COMMENTS_SORT } from '../components/CommentsSection/helpers/commentsSortQuery'
import { patchCommentNodePreserveChildren } from './commentTreePatch'
import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import { getMediaSourceUrl } from '../utils/media'
import type {
  CommentsSortUi,
  IAddCommentToProject,
  IGetCommentResponse,
  IProjectCommentCountersByType,
  ISetCommentReactionBody,
  IUpdateCommentBody,
} from './types/projects'
import type {
  GetGrowthToolCommentThreadPageArg,
  GetGrowthToolCommentsQueryArg,
  IGrowthToolLikeUserResource,
  IGrowthToolListItem,
  IGrowthToolModerationUserCandidate,
  IGrowthToolModerationUserCandidatesResponse,
  IGrowthToolResource,
  IGrowthToolUserRestrictionResource,
  GrowthToolUserRestrictionLevel,
  TCreateGrowthToolBody,
  TGetGrowthToolsCursorQueryParams,
  TGetGrowthToolsQueryParams,
  TGetGrowthToolCommentsResponse,
  TGrowthToolCommentThreadPage,
  TGrowthToolsCursorPage,
  TGrowthToolsListResponse,
  TUpdateGrowthToolBody,
} from './types/growthTools'

export const growthToolsTag = 'growthToolsTag' as const
const LIKES_PREVIEW_MAX = 3

const GROWTH_TOOL_LIST_ENDPOINTS = new Set([
  'getGrowthToolsPage',
  'getMyGrowthToolsPage',
  'getFavoriteGrowthToolsPage',
])

type TGrowthToolMutationCacheArg = {
  growthToolId: number
  cacheKey?: number | string
}

type TGrowthToolLikeCountersDraft = {
  viewer?: { is_liked?: boolean } | null
  counters: { likes?: number }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function normalizeUserShort(raw: unknown): unknown {
  if (!isRecord(raw)) return raw
  const avatarRaw = raw.avatar ?? raw.avatar_url ?? raw.avatarUrl
  return {
    ...raw,
    avatar: getMediaSourceUrl(avatarRaw),
  }
}

function normalizeCommentMyReaction(raw: unknown): number | null | undefined {
  if (raw === undefined) return undefined
  if (raw === null) return null
  if (raw === 'like') return 1
  if (raw === 'dislike') return -1
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function normalizeCommentNode(raw: unknown): IGetCommentResponse {
  if (raw == null || typeof raw !== 'object') {
    return raw as IGetCommentResponse
  }
  const c = raw as Record<string, unknown>
  const childrenRaw = c.children

  return {
    ...c,
    author: normalizeUserShort(c.author),
    my_reaction: normalizeCommentMyReaction(c.my_reaction),
    viewer: c.viewer && typeof c.viewer === 'object'
      ? { can_delete: (c.viewer as Record<string, unknown>).can_delete === true }
      : undefined,
    children: Array.isArray(childrenRaw)
      ? (childrenRaw as unknown[]).map((child) => normalizeCommentNode(child))
      : (childrenRaw as IGetCommentResponse['children']),
  } as IGetCommentResponse
}

function normalizeGrowthTool(raw: unknown): IGrowthToolResource {
  const toolRaw = isRecord(raw) ? raw : {}
  const countersRaw = isRecord(toolRaw.counters) ? toolRaw.counters : {}
  const viewerRaw = isRecord(toolRaw.viewer) ? toolRaw.viewer : null
  const author = toolRaw.author ? normalizeUserShort(toolRaw.author) : toolRaw.author
  const likesPreview = Array.isArray(toolRaw.likes_preview)
    ? toolRaw.likes_preview.map((u) => normalizeUserShort(u))
    : toolRaw.likes_preview
  const commentsRaw = countersRaw.comments

  const imageGallery = Array.isArray(toolRaw.image_gallery)
    ? (toolRaw.image_gallery as unknown[])
        .map((item) => getMediaSourceUrl(item))
        .filter((url): url is string => Boolean(url))
    : undefined

  return {
    ...toolRaw,
    image: getMediaSourceUrl(toolRaw.image),
    image_gallery: imageGallery,
    author,
    likes_preview: likesPreview ?? null,
    is_followed: Boolean(toolRaw.is_followed ?? viewerRaw?.is_followed),
    viewer: viewerRaw
      ? {
          is_followed: Boolean(viewerRaw.is_followed),
          is_liked: viewerRaw.is_liked === true,
          permissions: viewerRaw.permissions,
        }
      : null,
    counters: {
      likes: Number(countersRaw.likes ?? 0),
      views: Number(countersRaw.views ?? 0),
      subscribers: Number(countersRaw.subscribers ?? 0),
      comments:
        isRecord(commentsRaw)
          ? {
              total: Number(commentsRaw.total ?? 0),
              opinions: Number(commentsRaw.opinions ?? 0),
              suggestions: Number(commentsRaw.suggestions ?? 0),
              questions: Number(commentsRaw.questions ?? 0),
            }
          : Number(commentsRaw ?? 0),
    },
  } as IGrowthToolResource
}

function normalizeGrowthToolListItem(raw: unknown): IGrowthToolListItem {
  const item = normalizeGrowthTool(raw) as IGrowthToolListItem
  if (typeof item.counters.comments !== 'number') {
    item.counters.comments = Number((item.counters.comments as IProjectCommentCountersByType).total ?? 0)
  }
  return item
}

function parseGrowthToolsListResponse(response: unknown): TGrowthToolsListResponse {
  const payload = extractResponseData(response)
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeGrowthToolListItem(item))
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: unknown[] }).data.map((item) => normalizeGrowthToolListItem(item))
  }
  return []
}

function pickCursorFromNextLink(next: string): string | null {
  try {
    const fromUrl = new URL(next, BACKEND_API_URL).searchParams.get('cursor')
    if (fromUrl) return fromUrl
  } catch {
    // fall through
  }
  const match = next.match(/[?&]cursor=([^&]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function pickNextCursorFromPageRoot(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta = meta?.next_cursor
  if (fromMeta !== undefined && fromMeta !== null && String(fromMeta) !== '') {
    return String(fromMeta)
  }
  const links = body.links as Record<string, unknown> | undefined
  const next = links?.next
  if (typeof next === 'string' && next.length > 0) {
    return pickCursorFromNextLink(next)
  }
  return null
}

function parseGrowthToolsCursorPageResponse(response: unknown): TGrowthToolsCursorPage {
  const top = (response ?? {}) as Record<string, unknown>
  return {
    items: parseGrowthToolsListResponse(response),
    meta: { next_cursor: pickNextCursorFromPageRoot(top) },
  }
}

function serializeGrowthToolsQueryParams(params: TGetGrowthToolsCursorQueryParams | void): Record<string, string> {
  const raw = params ?? {}
  const result: Record<string, string> = {}
  const q = raw.query?.trim()
  if (q) result.query = q
  const sourceType = raw.source_type?.trim()
  if (sourceType && sourceType !== 'all') result.source_type = sourceType
  const contentType = raw.content_type?.trim()
  if (contentType && contentType !== 'all') result.content_type = contentType
  if (raw.cursor) result.cursor = raw.cursor
  if (typeof raw.limit === 'number' && Number.isFinite(raw.limit)) result.limit = String(raw.limit)
  if (raw.sort_by) result.sort_by = raw.sort_by
  if (raw.sort_direction) result.sort_direction = raw.sort_direction
  return result
}

function growthToolListProvidesTags(
  result: TGrowthToolsListResponse | TGrowthToolsCursorPage | undefined,
): Array<{ type: typeof growthToolsTag; id: string }> {
  if (!result) {
    return [{ type: growthToolsTag, id: 'LIST' }]
  }
  const items = Array.isArray(result) ? result : result.items ?? []
  return [
    { type: growthToolsTag, id: 'LIST' },
    ...items.map((tool) => ({ type: growthToolsTag, id: `id:${tool.id}` })),
  ]
}

function growthToolInvalidationTags(growthToolId: number) {
  return [
    { type: growthToolsTag, id: 'LIST' },
    { type: growthToolsTag, id: `id:${growthToolId}` },
  ]
}

function growthToolLikeListInvalidationTags(): Array<{ type: typeof growthToolsTag; id: string }> {
  return [{ type: growthToolsTag, id: 'LIST' }]
}

function patchGrowthToolDetail(
  dispatch: (action: unknown) => { undo: () => void },
  cacheKey: number | string | undefined,
  patcher: (draft: IGrowthToolResource) => void,
): { undo: () => void } | null {
  if (cacheKey == null) return null
  return dispatch(
    growthToolsApi.util.updateQueryData('getGrowthTool', { id: cacheKey }, (draft) => {
      if (draft) patcher(draft)
    }),
  ) as { undo: () => void }
}

function patchGrowthToolInListCaches(
  dispatch: (action: unknown) => { undo: () => void },
  getState: () => unknown,
  growthToolId: number,
  patcher: (draft: IGrowthToolListItem) => void,
): Array<{ undo: () => void }> {
  const patches: Array<{ undo: () => void }> = []
  const state = getState() as {
    growthToolsApi?: {
      queries?: Record<string, { endpointName?: string; originalArgs?: unknown; data?: unknown }>
    }
  }
  const queries = state.growthToolsApi?.queries ?? {}
  for (const queryEntry of Object.values(queries)) {
    const endpointName = queryEntry?.endpointName
    if (!endpointName || !GROWTH_TOOL_LIST_ENDPOINTS.has(endpointName) || queryEntry.data == null) continue
    const args = queryEntry.originalArgs
    const patch = dispatch(
      growthToolsApi.util.updateQueryData(
        endpointName as 'getGrowthToolsPage',
        args as TGetGrowthToolsCursorQueryParams | void,
        (draft) => {
          const item = draft.items?.find((tool) => tool.id === growthToolId)
          if (item) patcher(item)
        },
      ),
    )
    patches.push(patch)
  }
  return patches
}

function applyGrowthToolLikeToggle(draft: TGrowthToolLikeCountersDraft): void {
  const wasLiked = draft.viewer?.is_liked === true
  if (draft.viewer) {
    draft.viewer.is_liked = !wasLiked
  }
  draft.counters.likes = Math.max(0, Number(draft.counters.likes ?? 0) + (wasLiked ? -1 : 1))
}

function parseGrowthToolLikesResponse(
  response: unknown,
): { data: IGrowthToolLikeUserResource[]; meta: { next_cursor: string | null } } {
  const raw = isRecord(response) ? response : {}
  const data = Array.isArray(raw.data) ? raw.data : []
  const meta = isRecord(raw.meta) ? raw.meta : {}
  return {
    data: data.map((u) => {
      const item = isRecord(u) ? u : {}
      return {
        ...item,
        avatar: getMediaSourceUrl(item.avatar),
      } as IGrowthToolLikeUserResource
    }),
    meta: {
      next_cursor: meta.next_cursor != null ? String(meta.next_cursor) : null,
    },
  }
}

function parseCommentsListResponse(response: unknown): TGetGrowthToolCommentsResponse {
  const payload = extractResponseData(response)
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeCommentNode(item))
  }
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: unknown[] }).data.map((item) => normalizeCommentNode(item))
  }
  return []
}

function extractCommentThreadRowsAndMetaRoot(body: unknown): {
  rows: unknown[]
  metaRoot: Record<string, unknown>
} {
  if (body === null || typeof body !== 'object') return { rows: [], metaRoot: {} }
  const top = body as Record<string, unknown>
  if (Array.isArray(top.data)) return { rows: top.data as unknown[], metaRoot: top }
  const inner = top.data
  if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
    const lr = inner as Record<string, unknown>
    if (Array.isArray(lr.data)) return { rows: lr.data as unknown[], metaRoot: lr }
  }
  const once = extractResponseData(body)
  if (Array.isArray(once)) return { rows: once, metaRoot: top }
  if (once && typeof once === 'object' && Array.isArray((once as Record<string, unknown>).data)) {
    const o = once as Record<string, unknown>
    return { rows: o.data as unknown[], metaRoot: o }
  }
  return { rows: [], metaRoot: top }
}

function pickNextCursorFromBody(body: Record<string, unknown>): string | null {
  const meta = body.meta as Record<string, unknown> | undefined
  const fromMeta =
    meta?.next_cursor !== undefined && meta?.next_cursor !== null && meta.next_cursor !== ''
      ? String(meta.next_cursor)
      : ''
  if (fromMeta.length > 0) return fromMeta
  const links = body.links as Record<string, unknown> | undefined
  const next = links?.next
  if (typeof next === 'string' && next.length > 0) {
    try {
      const u = new URL(next, BACKEND_API_URL)
      return u.searchParams.get('cursor') ?? null
    } catch {
      return null
    }
  }
  return null
}

export const growthToolsApi = createApi({
  reducerPath: 'growthToolsApi',
  baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
  tagTypes: [growthToolsTag],
  endpoints: (builder) => ({
    getGrowthToolsPage: builder.query<TGrowthToolsCursorPage, TGetGrowthToolsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeGrowthToolsQueryParams(arg)
        return {
          url: `/${urlApi.growthTools}`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseGrowthToolsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => growthToolListProvidesTags(result),
    }),

    getMyGrowthToolsPage: builder.query<TGrowthToolsCursorPage, TGetGrowthToolsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeGrowthToolsQueryParams(arg)
        return {
          url: `/${urlApi.growthTools}/mine`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseGrowthToolsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => growthToolListProvidesTags(result),
    }),

    getUserGrowthToolsPage: builder.query<
      TGrowthToolsCursorPage,
      TGetGrowthToolsCursorQueryParams & { userId: number }
    >({
      query: ({ userId, ...arg }) => {
        const serialized = serializeGrowthToolsQueryParams(arg)
        return {
          url: `/${urlApi.users}/${userId}/growth-tools`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseGrowthToolsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => growthToolListProvidesTags(result),
    }),

    getFavoriteGrowthToolsPage: builder.query<TGrowthToolsCursorPage, TGetGrowthToolsCursorQueryParams | void>({
      query: (arg) => {
        const serialized = serializeGrowthToolsQueryParams(arg)
        return {
          url: `/${urlApi.growthTools}/subscriptions`,
          ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
        }
      },
      transformResponse: (response: unknown) => parseGrowthToolsCursorPageResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (result) => growthToolListProvidesTags(result),
    }),

    getGrowthTool: builder.query<IGrowthToolResource | null, { id: number | string }>({
      query: ({ id }) => ({
        url: `/${urlApi.growthTools}/${id}`,
      }),
      transformResponse: (response: unknown) => {
        const payload = extractResponseData(response)
        if (!payload || typeof payload !== 'object') return null
        return normalizeGrowthTool(payload)
      },
      transformErrorResponse: extractResponseError,
      providesTags: (result) =>
        result
          ? [
              { type: growthToolsTag, id: `id:${result.id}` },
              { type: growthToolsTag, id: `detail:${result.id}` },
            ]
          : [{ type: growthToolsTag, id: 'LIST' }],
    }),

    createGrowthTool: builder.mutation<IGrowthToolResource, { payload: TCreateGrowthToolBody }>({
      query: ({ payload }) => ({
        url: `/${urlApi.growthTools}`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: unknown) => normalizeGrowthTool(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
      invalidatesTags: [{ type: growthToolsTag, id: 'LIST' }],
    }),

    updateGrowthTool: builder.mutation<IGrowthToolResource, { id: number; payload: TUpdateGrowthToolBody }>({
      query: ({ id, payload }) => ({
        url: `/${urlApi.growthTools}/${id}/update`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: (response: unknown) => normalizeGrowthTool(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (result, _error, arg) => growthToolInvalidationTags(result?.id ?? arg.id),
    }),

    deleteGrowthTool: builder.mutation<void, { growthToolId: number }>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/delete`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
    }),

    uploadGrowthToolPreview: builder.mutation<unknown, { growthToolId: number; file: File }>({
      query: ({ growthToolId, file }) => {
        const formData = new FormData()
        formData.append('image', file)
        return {
          url: `/${urlApi.growthTools}/${growthToolId}/preview`,
          method: 'POST',
          body: formData,
        }
      },
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
    }),

    deleteGrowthToolPreview: builder.mutation<void, { growthToolId: number }>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/preview`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
    }),

    getGrowthToolImages: builder.query<
      { coverUrl: string | null; images: { url: string; mediaId?: number }[] },
      { growthToolId: number }
    >({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}`,
      }),
      transformResponse: (response: unknown) => {
        const data: any = extractResponseData(response)
        const coverUrl = getMediaSourceUrl(data?.image) || null
        const images = Array.isArray(data?.image_gallery)
          ? (data.image_gallery as unknown[]).flatMap((x: any) => {
              const url = getMediaSourceUrl(x)
              if (!url) return []
              const mediaId = x?.id != null ? Number(x.id) : undefined
              return [mediaId != null ? { url, mediaId } : { url }]
            })
          : []
        return { coverUrl, images }
      },
      transformErrorResponse: extractResponseError,
      // Must match growthToolInvalidationTags (`id:${id}`), not bare numeric id.
      providesTags: (_result, _error, arg) => [
        { type: growthToolsTag, id: `id:${arg.growthToolId}` },
      ],
    }),

    uploadGrowthToolGallery: builder.mutation<unknown[], { growthToolId: number; files: File[] }>({
      query: ({ growthToolId, files }) => {
        const formData = new FormData()
        files.forEach((file) => formData.append('images[]', file))
        return {
          url: `/${urlApi.growthTools}/${growthToolId}/gallery`,
          method: 'POST',
          body: formData,
        }
      },
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response)
        if (Array.isArray(data)) return data
        return Array.isArray(response) ? (response as unknown[]) : []
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
    }),

    deleteGrowthToolGalleryImage: builder.mutation<void, { growthToolId: number; mediaId: number }>({
      query: ({ growthToolId, mediaId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/gallery/${mediaId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
    }),

    trackGrowthToolView: builder.mutation<{ views: number }, { growthToolId: number; cacheKey?: number | string }>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/view`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => {
        const data = extractResponseData(response) as { views?: number } | null
        return { views: Number(data?.views ?? 0) }
      },
      transformErrorResponse: extractResponseError,
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled
          if (arg.cacheKey != null) {
            patchGrowthToolDetail(dispatch, arg.cacheKey, (draft) => {
              draft.counters.views = data.views
            })
          }
          patchGrowthToolInListCaches(dispatch, getState, arg.growthToolId, (draft) => {
            draft.counters.views = data.views
          })
        } catch {
          // view tracking should not disturb the page
        }
      },
    }),

    favoriteGrowthTool: builder.mutation<unknown, TGrowthToolMutationCacheArg>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/subscribe`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const detailPatch = patchGrowthToolDetail(dispatch, arg.cacheKey, (draft) => {
          if (draft.viewer) draft.viewer.is_followed = true
          draft.is_followed = true
          draft.counters.subscribers = Number(draft.counters.subscribers ?? 0) + 1
        })
        try {
          await queryFulfilled
        } catch {
          detailPatch?.undo()
        }
      },
    }),

    unfavoriteGrowthTool: builder.mutation<unknown, TGrowthToolMutationCacheArg>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/subscribe`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => growthToolInvalidationTags(arg.growthToolId),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const detailPatch = patchGrowthToolDetail(dispatch, arg.cacheKey, (draft) => {
          if (draft.viewer) draft.viewer.is_followed = false
          draft.is_followed = false
          draft.counters.subscribers = Math.max(0, Number(draft.counters.subscribers ?? 0) - 1)
        })
        try {
          await queryFulfilled
        } catch {
          detailPatch?.undo()
        }
      },
    }),

    likeGrowthTool: builder.mutation<unknown, TGrowthToolMutationCacheArg>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/like`,
        method: 'POST',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: () => growthToolLikeListInvalidationTags(),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const detailPatch = patchGrowthToolDetail(dispatch, arg.cacheKey, applyGrowthToolLikeToggle)
        const listPatches = patchGrowthToolInListCaches(dispatch, getState, arg.growthToolId, applyGrowthToolLikeToggle)
        try {
          await queryFulfilled
          if (arg.cacheKey != null) {
            const likesPreviewResult = await dispatch(
              growthToolsApi.endpoints.getGrowthToolLikes.initiate(
                { growthToolId: arg.growthToolId, limit: LIKES_PREVIEW_MAX, cursor: null },
                { forceRefetch: true },
              ),
            ).unwrap()
            patchGrowthToolDetail(dispatch, arg.cacheKey, (draft) => {
              draft.likes_preview = (likesPreviewResult.data ?? []).slice(0, LIKES_PREVIEW_MAX)
            })
          }
        } catch {
          detailPatch?.undo()
          listPatches.forEach((p) => p.undo())
        }
      },
    }),

    unlikeGrowthTool: builder.mutation<unknown, TGrowthToolMutationCacheArg>({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/like`,
        method: 'DELETE',
      }),
      transformResponse: (response: unknown) => extractResponseData(response),
      transformErrorResponse: extractResponseError,
      invalidatesTags: () => growthToolLikeListInvalidationTags(),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        const detailPatch = patchGrowthToolDetail(dispatch, arg.cacheKey, applyGrowthToolLikeToggle)
        const listPatches = patchGrowthToolInListCaches(dispatch, getState, arg.growthToolId, applyGrowthToolLikeToggle)
        try {
          await queryFulfilled
          if (arg.cacheKey != null) {
            const likesPreviewResult = await dispatch(
              growthToolsApi.endpoints.getGrowthToolLikes.initiate(
                { growthToolId: arg.growthToolId, limit: LIKES_PREVIEW_MAX, cursor: null },
                { forceRefetch: true },
              ),
            ).unwrap()
            patchGrowthToolDetail(dispatch, arg.cacheKey, (draft) => {
              draft.likes_preview = (likesPreviewResult.data ?? []).slice(0, LIKES_PREVIEW_MAX)
            })
          }
        } catch {
          detailPatch?.undo()
          listPatches.forEach((p) => p.undo())
        }
      },
    }),

    getGrowthToolLikes: builder.query<
      { data: IGrowthToolLikeUserResource[]; meta: { next_cursor: string | null } },
      { growthToolId: number; limit?: number; cursor?: string | null }
    >({
      query: ({ growthToolId, limit, cursor }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/likes`,
        params: {
          ...(limit != null ? { limit: String(limit) } : {}),
          ...(cursor ? { cursor } : {}),
        },
      }),
      transformResponse: (response: unknown) => parseGrowthToolLikesResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [{ type: growthToolsTag, id: `likes:${arg.growthToolId}` }],
    }),

    getGrowthToolComments: builder.query<TGetGrowthToolCommentsResponse, GetGrowthToolCommentsQueryArg>({
      query: ({ growthToolId, type, parentId, limit, cursor, sort }) => {
        const params: Record<string, string> = {}
        if (parentId != null) params.parent_id = String(parentId)
        if (limit != null) params.limit = String(limit)
        if (cursor != null && cursor !== '') params.cursor = String(cursor)
        if (parentId == null) {
          appendCommentsSortQueryParams(params, sort ?? DEFAULT_COMMENTS_SORT)
        }
        return {
          url: `/${urlApi.growthTools}/${growthToolId}/comments/${type ? type : ''}`,
          ...(Object.keys(params).length > 0 ? { params } : {}),
        }
      },
      transformResponse: (response: unknown): TGetGrowthToolCommentsResponse => parseCommentsListResponse(response),
      transformErrorResponse: extractResponseError,
      providesTags: [growthToolsTag],
    }),

    getGrowthToolCommentThreadPage: builder.query<TGrowthToolCommentThreadPage, GetGrowthToolCommentThreadPageArg>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const params: Record<string, string> = {
          parent_id: String(arg.parentId),
        }
        if (arg.cursor) params.cursor = arg.cursor
        if (arg.limit != null) params.limit = String(arg.limit)

        const res = await baseQuery({
          url: `/${urlApi.growthTools}/${arg.growthToolId}/comments/${arg.type ? arg.type : ''}`,
          params,
        })
        if (res.error) return { error: res.error }
        const { rows, metaRoot } = extractCommentThreadRowsAndMetaRoot(res.data)
        const items = rows.map((item) => normalizeCommentNode(item))
        const nextCursor = pickNextCursorFromBody(metaRoot)
        return { data: { items, nextCursor } }
      },
    }),

    addCommentToGrowthTool: builder.mutation<
      IGetCommentResponse,
      { growthToolId: number; type: string; body: IAddCommentToProject }
    >({
      query: ({ growthToolId, type, body }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/comments/${type ? type : ''}`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
    }),

    updateGrowthToolComment: builder.mutation<IGetCommentResponse, { commentId: number; body: IUpdateCommentBody }>({
      query: ({ commentId, body }) => ({
        url: `/${urlApi.comments}/${commentId}/update`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
    }),

    setGrowthToolCommentReaction: builder.mutation<
      IGetCommentResponse,
      {
        commentId: number
        body: ISetCommentReactionBody
        cache?: {
          kind: 'growthTool'
          growthToolId: number
          type: string
          sort?: CommentsSortUi
          threadParentId?: number
        }
      }
    >({
      query: ({ commentId, body }) => ({
        url: `/${urlApi.comments}/${commentId}/reaction`,
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
      transformErrorResponse: extractResponseError,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (!arg.cache) return
          const normalized = normalizeCommentNode(data)
          dispatch(
            growthToolsApi.util.updateQueryData(
              'getGrowthToolComments',
              {
                growthToolId: arg.cache.growthToolId,
                type: arg.cache.type,
                limit: CURSOR_LIST_PAGE_SIZE,
                sort: arg.cache.sort ?? DEFAULT_COMMENTS_SORT,
              },
              (draft) => patchCommentNodePreserveChildren(original(draft) ?? [...draft], arg.commentId, normalized),
            ),
          )
          if (arg.cache.threadParentId != null) {
            dispatch(
              growthToolsApi.util.updateQueryData(
                'getGrowthToolCommentThreadPage',
                {
                  growthToolId: arg.cache.growthToolId,
                  type: arg.cache.type,
                  parentId: arg.cache.threadParentId,
                  limit: CURSOR_LIST_PAGE_SIZE,
                  cursor: null,
                },
                (draft) => {
                  draft.items = patchCommentNodePreserveChildren(draft.items ?? [], arg.commentId, normalized)
                },
              ),
            )
          }
        } catch {
          // ignore
        }
      },
    }),

    deleteGrowthToolComment: builder.mutation<void, { commentId: number }>({
      query: ({ commentId }) => ({
        url: `/${urlApi.comments}/${commentId}/delete`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
    }),

    getGrowthToolModerationUserCandidates: builder.query<
      IGrowthToolModerationUserCandidatesResponse,
      { growthToolId: number }
    >({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/moderation/user-candidates`,
      }),
      transformResponse: (response: unknown): IGrowthToolModerationUserCandidatesResponse => {
        const root = response as { meta?: { total?: unknown } } | null
        const payload = extractResponseData(response)
        const totalRaw = root?.meta?.total
        const itemsRaw = Array.isArray(payload) ? payload : []
        const items: IGrowthToolModerationUserCandidate[] = []

        for (const item of itemsRaw as any[]) {
          const user = normalizeUserShort(item) as Record<string, unknown> | null
          if (!user || typeof user !== 'object') continue
          const id = Number(item?.user_id ?? user.id)
          if (!Number.isSafeInteger(id) || id <= 0) continue

          items.push({
            id,
            user_id: id,
            slug: user.slug != null ? String(user.slug) : null,
            fullname: user.fullname != null ? String(user.fullname) : null,
            shortname: user.shortname != null ? String(user.shortname) : null,
            avatar: user.avatar != null ? String(user.avatar) : null,
            deleted: Boolean(user.deleted),
            is_online: user.is_online === true,
            restrictable: Boolean(item?.restrictable ?? true),
            restrictable_reason:
              typeof item?.restrictable_reason === 'string' ? item.restrictable_reason : null,
          })
        }

        const total =
          typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? totalRaw : items.length
        return { items, total }
      },
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: growthToolsTag, id: `moderation-candidates:${arg.growthToolId}` },
      ],
    }),

    getGrowthToolRestrictions: builder.query<
      IGrowthToolUserRestrictionResource[],
      { growthToolId: number }
    >({
      query: ({ growthToolId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/restrictions`,
      }),
      transformResponse: (response: unknown) => {
        const payload = extractResponseData(response)
        if (!Array.isArray(payload)) return []
        return payload
          .map((item: any) => {
            const user = normalizeUserShort(item?.user) as IGrowthToolUserRestrictionResource['user'] | null
            const createdBy = normalizeUserShort(item?.created_by) as
              | IGrowthToolUserRestrictionResource['created_by']
              | null
            if (!user) return null
            const userId = Number(item?.user_id ?? user.id)
            if (!Number.isSafeInteger(userId) || userId <= 0) return null
            const level =
              item?.level === 'full' || item?.level === 'comments' ? item.level : 'comments'
            return {
              user,
              user_id: userId,
              level,
              reason: item?.reason != null ? String(item.reason) : null,
              created_at: String(item?.created_at ?? ''),
              created_by: createdBy ?? user,
            } satisfies IGrowthToolUserRestrictionResource
          })
          .filter((item): item is IGrowthToolUserRestrictionResource => item !== null)
      },
      transformErrorResponse: extractResponseError,
      providesTags: (_result, _error, arg) => [
        { type: growthToolsTag, id: `restrictions:${arg.growthToolId}` },
      ],
    }),

    createGrowthToolRestriction: builder.mutation<
      IGrowthToolUserRestrictionResource,
      {
        growthToolId: number
        body: { user_id: number; level: GrowthToolUserRestrictionLevel; reason?: string }
      }
    >({
      query: ({ growthToolId, body }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/restrictions`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => {
        const item = extractResponseData(response) as any
        const user = normalizeUserShort(item?.user) as IGrowthToolUserRestrictionResource['user'] | null
        const createdBy = normalizeUserShort(item?.created_by) as
          | IGrowthToolUserRestrictionResource['created_by']
          | null
        if (!user) {
          throw new Error('Invalid restriction response')
        }
        const userId = Number(item?.user_id ?? user.id)
        if (!Number.isSafeInteger(userId) || userId <= 0) {
          throw new Error('Invalid restriction user_id')
        }
        const level = item?.level === 'full' || item?.level === 'comments' ? item.level : 'comments'
        return {
          user,
          user_id: userId,
          level,
          reason: item?.reason != null ? String(item.reason) : null,
          created_at: String(item?.created_at ?? ''),
          created_by: createdBy ?? user,
        } satisfies IGrowthToolUserRestrictionResource
      },
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        ...growthToolInvalidationTags(arg.growthToolId),
        { type: growthToolsTag, id: `restrictions:${arg.growthToolId}` },
        { type: growthToolsTag, id: `moderation-candidates:${arg.growthToolId}` },
      ],
    }),

    deleteGrowthToolRestriction: builder.mutation<void, { growthToolId: number; userId: number }>({
      query: ({ growthToolId, userId }) => ({
        url: `/${urlApi.growthTools}/${growthToolId}/restrictions/${userId}`,
        method: 'DELETE',
      }),
      transformErrorResponse: extractResponseError,
      invalidatesTags: (_result, _error, arg) => [
        ...growthToolInvalidationTags(arg.growthToolId),
        { type: growthToolsTag, id: `restrictions:${arg.growthToolId}` },
        { type: growthToolsTag, id: `moderation-candidates:${arg.growthToolId}` },
      ],
    }),
  }),
})

export const {
  useGetGrowthToolsPageQuery,
  useLazyGetGrowthToolsPageQuery,
  useGetMyGrowthToolsPageQuery,
  useLazyGetMyGrowthToolsPageQuery,
  useLazyGetUserGrowthToolsPageQuery,
  useGetFavoriteGrowthToolsPageQuery,
  useLazyGetFavoriteGrowthToolsPageQuery,
  useGetGrowthToolQuery,
  useLazyGetGrowthToolQuery,
  useCreateGrowthToolMutation,
  useUpdateGrowthToolMutation,
  useDeleteGrowthToolMutation,
  useUploadGrowthToolPreviewMutation,
  useDeleteGrowthToolPreviewMutation,
  useGetGrowthToolImagesQuery,
  useUploadGrowthToolGalleryMutation,
  useDeleteGrowthToolGalleryImageMutation,
  useTrackGrowthToolViewMutation,
  useFavoriteGrowthToolMutation,
  useUnfavoriteGrowthToolMutation,
  useLikeGrowthToolMutation,
  useUnlikeGrowthToolMutation,
  useGetGrowthToolLikesQuery,
  useLazyGetGrowthToolLikesQuery,
  useGetGrowthToolCommentsQuery,
  useLazyGetGrowthToolCommentsQuery,
  useGetGrowthToolCommentThreadPageQuery,
  useLazyGetGrowthToolCommentThreadPageQuery,
  useAddCommentToGrowthToolMutation,
  useUpdateGrowthToolCommentMutation,
  useSetGrowthToolCommentReactionMutation,
  useDeleteGrowthToolCommentMutation,
  useGetGrowthToolModerationUserCandidatesQuery,
  useGetGrowthToolRestrictionsQuery,
  useCreateGrowthToolRestrictionMutation,
  useDeleteGrowthToolRestrictionMutation,
} = growthToolsApi

export type { TGetGrowthToolsQueryParams }
